import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { store } from './store';
import './index.css';
import App from './App.tsx';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import monitoring from './services/monitoring';

// Initialize monitoring services
monitoring.init({
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  googleAnalyticsId: import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
  enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  enablePerformanceMonitoring:
    import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === 'true',
  enableConsoleErrors: import.meta.env.DEV, // Only in development
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
    },
    mutations: {
      onError: (error: Error) => {
        // Track mutation errors
        monitoring.captureError({
          message: `React Query Mutation Error: ${error.message}`,
          severity: 'high',
          tags: { type: 'react_query_mutation_error' },
          extra: { error },
        });
      },
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
